import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import axios from 'axios';

const Admin = () => {
  const [contestData, setContestData] = useState({
    isActive: false,
    startDate: '',
    endDate: '',
    totalEntries: 0,
    recentEntries: []
  });
  
  const [weeklyContent, setWeeklyContent] = useState([
    { day: 'Monday', image: null, question: '', options: ['', '', '', ''], correctAnswer: 0 },
    { day: 'Tuesday', image: null, question: '', options: ['', '', '', ''], correctAnswer: 0 },
    { day: 'Wednesday', image: null, question: '', options: ['', '', '', ''], correctAnswer: 0 },
    { day: 'Thursday', image: null, question: '', options: ['', '', '', ''], correctAnswer: 0 },
    { day: 'Friday', image: null, question: '', options: ['', '', '', ''], correctAnswer: 0 },
    { day: 'Saturday', image: null, question: '', options: ['', '', '', ''], correctAnswer: 0 },
    { day: 'Sunday', image: null, question: '', options: ['', '', '', ''], correctAnswer: 0 }
  ]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  const { user, isAuthenticated } = useSelector(state => state.auth);

  // Redirect if not authenticated or not admin
  if (!isAuthenticated || !user?.isAdmin) {
    return <Navigate to="/" replace />;
  }

  const handleImageUpload = (day, e) => {
    const file = e.target.files[0];
    if (file) {
      setWeeklyContent(prev => prev.map(content => 
        content.day === day ? { ...content, image: file } : content
      ));
    }
  };

  const handleQuestionChange = (day, value) => {
    setWeeklyContent(prev => prev.map(content => 
      content.day === day ? { ...content, question: value } : content
    ));
  };

  const handleOptionChange = (day, optionIndex, value) => {
    setWeeklyContent(prev => prev.map(content => 
      content.day === day 
        ? { 
            ...content, 
            options: content.options.map((opt, idx) => 
              idx === optionIndex ? value : opt
            )
          } 
        : content
    ));
  };

  const handleCorrectAnswerChange = (day, value) => {
    setWeeklyContent(prev => prev.map(content => 
      content.day === day ? { ...content, correctAnswer: parseInt(value) } : content
    ));
  };

  const handleSubmitWeeklyContent = async () => {
    try {
      const formData = new FormData();
      weeklyContent.forEach((content, index) => {
        formData.append(`image_${content.day}`, content.image);
        formData.append(`question_${content.day}`, content.question);
        formData.append(`options_${content.day}`, JSON.stringify(content.options));
        formData.append(`correctAnswer_${content.day}`, content.correctAnswer);
      });

      await axios.post('/api/admin/weekly-content', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setSuccessMessage('Weekly content updated successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError('Failed to update weekly content');
    }
  };

  const handleToggleContest = async () => {
    try {
      const response = await axios.post('/api/admin/toggle-contest', {
        isActive: !contestData.isActive
      });
      setContestData(prev => ({
        ...prev,
        isActive: response.data.isActive
      }));
    } catch (err) {
      setError('Failed to toggle contest status');
    }
  };

  const handleDateChange = async (e) => {
    const { name, value } = e.target;
    try {
      const response = await axios.post('/api/admin/update-dates', {
        [name]: value
      });
      setContestData(prev => ({
        ...prev,
        [name]: value
      }));
    } catch (err) {
      setError('Failed to update contest dates');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 p-8">
        <div className="text-center">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-8">Contest Administration</h1>
        
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
            {successMessage}
          </div>
        )}

        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Contest Controls</h2>
          
          <div className="flex items-center mb-4">
            <span className="mr-4">Contest Status:</span>
            <button
              onClick={handleToggleContest}
              className={`px-4 py-2 rounded-md ${
                contestData.isActive 
                  ? 'bg-green-500 hover:bg-green-600' 
                  : 'bg-red-500 hover:bg-red-600'
              } text-white`}
            >
              {contestData.isActive ? 'Active' : 'Inactive'}
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date
              </label>
              <input
                type="datetime-local"
                name="startDate"
                value={contestData.startDate}
                onChange={handleDateChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Date
              </label>
              <input
                type="datetime-local"
                name="endDate"
                value={contestData.endDate}
                onChange={handleDateChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Weekly Content Setup</h2>
          
          {weeklyContent.map((content) => (
            <div key={content.day} className="mb-8 p-4 border rounded">
              <h3 className="text-lg font-medium mb-4">{content.day}</h3>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Image
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(content.day, e)}
                  className="block w-full text-sm text-gray-500
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-md file:border-0
                    file:text-sm file:font-semibold
                    file:bg-blue-50 file:text-blue-700
                    hover:file:bg-blue-100"
                />
                {content.image && (
                  <p className="mt-2 text-sm text-gray-600">
                    Selected: {content.image.name}
                  </p>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Question
                </label>
                <input
                  type="text"
                  value={content.question}
                  onChange={(e) => handleQuestionChange(content.day, e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder="Enter question"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Options
                </label>
                {content.options.map((option, idx) => (
                  <input
                    key={idx}
                    type="text"
                    value={option}
                    onChange={(e) => handleOptionChange(content.day, idx, e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 mb-2"
                    placeholder={`Option ${idx + 1}`}
                  />
                ))}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Correct Answer
                </label>
                <select
                  value={content.correctAnswer}
                  onChange={(e) => handleCorrectAnswerChange(content.day, e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                >
                  {content.options.map((_, idx) => (
                    <option key={idx} value={idx}>
                      Option {idx + 1}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ))}

          <button
            onClick={handleSubmitWeeklyContent}
            className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded"
          >
            Save Weekly Content
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">Contest Statistics</h2>
          
          <div className="mb-6">
            <p className="text-lg">Total Entries: {contestData.totalEntries}</p>
          </div>

          <h3 className="font-medium mb-3">Recent Entries</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Location
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {contestData.recentEntries.map((entry, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {new Date(entry.date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {entry.user}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {entry.location}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Admin;
